import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { supabase } from './supabase'
import i18n, { SUPPORTED_LANGUAGES } from '@/i18n'
import type { User } from '@supabase/supabase-js'

interface Profile {
  id: string
  display_name: string
  onboarding_completed: boolean
  onboarded_at?: string | null
  bio?: string | null
  location_text?: string | null
  location_point?: unknown
  reader_portrait?: string | null
  avatar_url?: string | null
  created_at?: string | null
  featured_text?: string | null
  featured_text_public?: boolean | null
  featured_text_updated_at?: string | null
  featured_text_visibility?: string | null
  subscription_tier?: string | null
  trial_ends_at?: string | null
  language?: string | null
  map_lat?: number | null
  map_lng?: number | null
}

interface AuthContextType {
  user: User | null
  profile: Profile | null
  balance: number | null
  loading: boolean
  isNewUser: boolean
  refreshProfile: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  balance: null,
  loading: true,
  isNewUser: false,
  refreshProfile: async () => {},
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [balance, setBalance] = useState<number | null>(null)
  const [isNewUser, setIsNewUser] = useState(false)
  const [loading, setLoading] = useState(true)
  // Tracks which user we've applied the saved language for, so the
  // DB preference is applied once per login and never overrides a
  // manual language switch within the session.
  const langSyncedForRef = useRef<string | null>(null)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        setProfile(null)
        setBalance(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error fetching profile:', error)
      } else {
        setProfile(data)
        // New user if onboarding not completed
        setIsNewUser(!(data?.onboarding_completed || data?.onboarded_at))
      }

      // Fetch cherry balance
      const { data: balanceData, error: balanceError } = await supabase
        .from('user_cherries_balance')
        .select('balance')
        .eq('user_id', userId)
        .single()

      if (balanceError) {
        console.error('Error fetching balance:', balanceError)
      } else {
        setBalance(balanceData?.balance ?? 0)
      }

      // Apply the saved UI language (user_settings.language, written by
      // SettingsPage) once per login so it follows the user across devices.
      // Precedence: an explicit localStorage choice ('cherry_lang', already
      // applied by the i18next detector) wins over the DB value — otherwise
      // the stale DB language would undo every manual switch on next login.
      // In that case we async-update the DB row to match. Only when no
      // localStorage choice exists do we apply the DB language (the detector
      // then caches it for the session).
      if (langSyncedForRef.current !== userId) {
        langSyncedForRef.current = userId
        const localLang = (() => {
          try {
            return window.localStorage.getItem('cherry_lang')
          } catch {
            return null
          }
        })()
        const localBase = localLang?.split('-')[0] ?? null
        const isSupportedLocal =
          !!localBase && (SUPPORTED_LANGUAGES as readonly string[]).includes(localBase)
        const { data: settingsData } = await supabase
          .from('user_settings')
          .select('language')
          .eq('user_id', userId)
          .maybeSingle()
        const savedLang = settingsData?.language
        if (isSupportedLocal && localBase) {
          // Local choice wins: keep it and sync the DB row to match so other
          // devices pick it up on their next login.
          if (savedLang && savedLang !== localBase) {
            void supabase
              .from('user_settings')
              .update({ language: localBase })
              .eq('user_id', userId)
              .then(({ error }) => {
                if (error) console.warn('Could not sync language to DB:', error)
              })
          }
        } else if (savedLang && savedLang !== i18n.language) {
          i18n.changeLanguage(savedLang)
        }
      }
    } catch (err) {
      console.error('Profile fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  async function refreshProfile() {
    if (user) {
      await fetchProfile(user.id)
    }
  }

  async function signOut() {
    await supabase.auth.signOut()
    langSyncedForRef.current = null
    setUser(null)
    setProfile(null)
    setBalance(null)
  }

  return (
    <AuthContext.Provider value={{ user, profile, balance, loading, isNewUser, refreshProfile, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
